import { BTButton, BTPopover } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";

import { BdsIconHomeowners, BdsIconPlus, BdsIconSubsVendors } from "@buildertrend/components";

import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import "commonComponents/entity/media/MediaViewingPermissionReadonly/MediaViewingPermissionReadonly.less";

export interface IMediaViewingPermissionReadonlyProps {
    showClient: boolean;
    showSubs: boolean;
    showAllSubs?: boolean;
    entityId: number;
    entityType: "folder" | "file";
    justify?: "start" | "end" | "center" | "space-around" | "space-between";
    noWrap?: boolean;
    className?: string;
    canSetPermissions?: boolean;
    showCanAdd?: boolean;
    onEditPermissionsClick?: (id: number) => void;
}

function MediaViewingPermissionReadonly({
    showClient,
    showSubs,
    showAllSubs = true,
    justify = "center",
    noWrap = false,
    className,
    canSetPermissions,
    entityId,
    entityType,
    showCanAdd,
    onEditPermissionsClick,
}: IMediaViewingPermissionReadonlyProps) {
    const subsText = showAllSubs ? "Subs/trades can view" : "Some subs/trades can view";
    const clientsText = "Client can view";

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (onEditPermissionsClick) {
            onEditPermissionsClick(entityId);
        }
    };

    const subButton = (
        <BTPopover content={subsText} widthType="unrestricted">
            <BTButton
                // Using a non-clickable BTButton since we shouldn't use a BTPopover on a span or div due to accessibility
                icon={<BdsIconSubsVendors />}
                aria-label="Set subs/vendors permissions"
                className="MediaViewingPermissionsReadonly-icon"
                data-testid={`${entityType}-permissions-subs-${entityId}`}
                onClick={canSetPermissions ? handleClick : undefined}
                type="tertiary"
                style={{ marginRight: 0 }}
            />
        </BTPopover>
    );

    const clientButton = (
        <BTPopover content={clientsText}>
            <BTButton
                // Using a non-clickable BTButton since we shouldn't use a BTPopover on a span or div due to accessibility
                icon={<BdsIconHomeowners />}
                aria-label="Set client permissions"
                className="MediaViewingPermissionsReadonly-icon"
                data-testid={`${entityType}-permissions-clients-${entityId}`}
                onClick={canSetPermissions ? handleClick : undefined}
                type="tertiary"
                style={{ marginRight: 4 }}
            />
        </BTPopover>
    );

    return (
        <BTRow
            className={cn(className, "MediaViewingPermissionsReadonly", {
                "MediaViewingPermissionsReadonly-nowrap": noWrap,
            })}
            gutter={8}
            align="middle"
            justify={justify}
        >
            {showSubs && subButton}
            {showClient && clientButton}
            {!showSubs && !showClient && showCanAdd && canSetPermissions && (
                <BTPopover content="Add permissions">
                    <BTButton
                        icon={<BdsIconPlus />}
                        onClick={handleClick}
                        data-testid={`add-${entityType}-permissions-${entityId}`}
                        type="tertiary"
                    >
                        Add
                    </BTButton>
                </BTPopover>
            )}
        </BTRow>
    );
}

export default MediaViewingPermissionReadonly;
